import React, { useEffect, useState } from "react";
import { ChoiceGroup, IChoiceGroupOption } from "@fluentui/react";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import {
  AdvancedDocumentMetadata,
  DocumentMetadata,
  getDocumentAdvancedCustomXml,
} from "../../../utils/helpers/DocumentAdvancedCustomXmlHelper";
import { ObjectPanelTabs } from "../co-panel/ObjectPanelComponent";
import { DocumentPart } from "../../../api/models";
import NotificationManager from "../../../common/notification/NotificationManager";
import { fetchDocumentPartDetails } from "../../../api/document/DocumentsPart";

interface CoObjectAdvancedTableProps {
  objectAdvanced: AdvancedDocumentMetadata;
  updateCoObjectAdvancedTable: (obj: AdvancedDocumentMetadata, tabValid, modified: boolean) => void;
  tab: ObjectPanelTabs;
  isEdit: boolean;
  isLocked: boolean;
  documentPartId: string;
  isFileExcelExisting?: boolean;
  isComponentInstance?: boolean;
}

interface TableAdvanced {
  editAmountCustomXML: DocumentMetadata;
  documentPart: DocumentPart;
}

export const getAdvancedTable = async (
  newDocPart: DocumentPart,
  isEdit: boolean,
  objectAdvanced: AdvancedDocumentMetadata
): Promise<AdvancedDocumentMetadata> => {
  if (!isEdit && !(newDocPart.multiLinked && !newDocPart.root)) {
    // if new obj and is inheritable
    return {
      ...objectAdvanced,
      choice: objectAdvanced?.choice ?? "SOURCE",
      inheritFormat: false,
      locale: newDocPart.metadata?.formatMetadata.locale,
    };
  } else if (!(newDocPart.multiLinked && !newDocPart.root)) {
    //if edit and inheritable
    let choice = objectAdvanced?.choice ?? "SOURCE";
    return {
      ...objectAdvanced,
      choice: choice,
      inheritFormat: choice !== "SOURCE",
      locale: newDocPart.metadata?.formatMetadata.locale,
    };
  } else {
    // if edit and not inheritable
    return {
      ...objectAdvanced,
      choice: "SOURCE",
      inheritFormat: false,
      locale: newDocPart.metadata?.formatMetadata.locale,
    };
  }
};

export const CoObjectAdvancedPanelTable: React.FunctionComponent<CoObjectAdvancedTableProps> = (props) => {
  const {
    objectAdvanced,
    updateCoObjectAdvancedTable,
    isEdit,
    isLocked,
    documentPartId,
    isFileExcelExisting,
    isComponentInstance,
  } = props;
  const [state, setState] = useState<TableAdvanced>({ editAmountCustomXML: null, documentPart: null });
  const { editAmountCustomXML, documentPart } = state;
  const options: IChoiceGroupOption[] = [
    {
      key: "DOCUMENT_ROOT",
      text: formatMessage("cof.createnewobject.documentroot"),
      disabled: !objectAdvanced?.inheritFormat && (!documentPart || (documentPart.multiLinked && !documentPart.root)),
    },
    { key: "SOURCE", text: formatMessage("cof.createnewobject.source") },
  ];

  useEffect(() => {
    getInitialDocumentSettings();
  }, []);

  const onChangeFormattingAs = (_: React.SyntheticEvent<HTMLElement>, option: IChoiceGroupOption) => {
    if (option.key === "DOCUMENT_ROOT") {
      if (documentPart.root) {
        const advancedDocument: AdvancedDocumentMetadata = {
          ...editAmountCustomXML.advanced,
          choice: option.key,
          inheritFormat: true,
        };
        updateCoObjectAdvancedTable(advancedDocument, validateFields(advancedDocument, documentPart), true);
      } else {
        const advancedDocument: AdvancedDocumentMetadata = {
          numberFormat: documentPart.metadata.formatMetadata.numberFormat.format,
          typeNumber: documentPart.metadata.formatMetadata.numberType,
          percentageFormat: documentPart.metadata.formatMetadata.percentageFormat.format,
          typePercentage: documentPart.metadata.formatMetadata.percentageType,
          dateFormat: documentPart.metadata.formatMetadata.dateFormat.format,
          typeDate: documentPart.metadata.formatMetadata.dateType,
          choice: option.key,
          id: objectAdvanced?.id,
          locale: documentPart.metadata.formatMetadata.locale,
          inheritFormat: true,
        };
        updateCoObjectAdvancedTable(advancedDocument, validateFields(advancedDocument, documentPart), true);
      }
    } else {
      const advancedDocument: AdvancedDocumentMetadata = {
        ...objectAdvanced,
        choice: option.key,
        inheritFormat: false,
        locale: documentPart.metadata.formatMetadata.locale,
      };
      updateCoObjectAdvancedTable(advancedDocument, validateFields(advancedDocument, documentPart), true);
    }
  };

  function validateFields(obj: AdvancedDocumentMetadata, dp: DocumentPart): ObjectPanelTabs {
    let isValid = true;
    if (obj == null) isValid = false;
    else {
      const { choice } = obj;

      if (!choice) isValid = false;
    }

    if (obj.inheritFormat && (!dp || (dp.multiLinked && !dp.root))) isValid = false;
    return { key: props.tab.key, name: props.tab.name, isValid: isValid };
  }

  async function getInitialDocumentSettings() {
    const editDocumentCustomXML = await getDocumentAdvancedCustomXml();
    const newDocPart = await fetchDocumentPartDetails(documentPartId);
    setState((s) => ({ ...s, editAmountCustomXML: editDocumentCustomXML, documentPart: newDocPart }));

    if (objectAdvanced?.inheritFormat && (!newDocPart || (newDocPart.multiLinked && !newDocPart.root))) {
      NotificationManager.addNotification({
        title: "",
        message: formatMessage("cof.createnewobject.inheritformat.error"),
        type: "error",
      });
    }

    const advancedDocument = await getAdvancedTable(newDocPart, isEdit, objectAdvanced);
    updateCoObjectAdvancedTable(advancedDocument, validateFields(advancedDocument, newDocPart), !isEdit);
  }

  return (
    <>
      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <ChoiceGroup
              selectedKey={objectAdvanced?.choice ?? "SOURCE"}
              styles={{
                flexContainer: { display: "flex", justifyContent: "left", columnGap: "0.5rem", flexWrap: "wrap" },
              }}
              options={options}
              onChange={onChangeFormattingAs}
              label={formatMessage("cof.createnewobject.formatstarting")}
              disabled={!isFileExcelExisting || isComponentInstance || isLocked}
            />
          </div>
        </div>
      </div>
    </>
  );
};
